import React, { useMemo } from 'react';
import {
  X,
  ArrowLeft,
  Clock,
  CheckCircle,
  XCircle,
  Ban,
  ArrowDownRight,
  ArrowUpRight,
  Package,
  Gamepad2,
  AlertTriangle,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/shadcn/card';
import { Separator } from '@/components/ui/shadcn/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/shadcn/table';
import { ScrollArea } from '@/components/ui/shadcn/scroll-area';
import { Transaction, TransactionAllocation } from '../../../types';
import { cn } from '../../../utils';
import {
  summarizeTransactionAllocations,
  transactionNeedsAllocationsFetch,
} from '../../../utils/transactionAllocationSummary';
import {
  getRecordedFailureReason,
  shouldShowRecordedFailureBanner,
} from '../../../utils/transactionFailureReason';
import IntegrationLogsLink from '../integrationLogs/IntegrationLogsLink';
import GatewayBadge from '../../ui/GatewayBadge';
import { TransactionCashFlowTimelineTree, formatInTimezone, useTimezone } from '@kioskgaming/ui';
import { buildTransactionCashFlowTimeline } from '../../../utils/transactionCashFlowTimeline';
import { buildTransactionLifecycleFlow, getLifecycleFlowKindLabel } from '../../../utils/transactionLifecycleFlow';
import { getBalanceToCreditsConversionDisplay } from '../../../utils/balanceToCreditsConversionDisplay';
import {
  getTransactionDisplayType,
  getTransactionListAmountColumns,
  getTransactionAmountDisplay,
  formatCreditsAmount,
  formatCashAmount,
} from '../../../utils/transactionDisplayUtils';
import {
  applyWithdrawalPayoutRefundDetailAmount,
  getAdminTransactionStatusDisplay,
  getWithdrawalPayoutRefundInfo,
} from '../../../utils/withdrawalPayoutRefundDisplay';
import TransactionAllocationsPanel from './TransactionAllocationsPanel';
import TransactionFraudFlagBanner from './TransactionFraudFlagBanner';
import TransactionLifecycleFlow from './TransactionLifecycleFlow';
import GgusonepayManualReviewPanel from './GgusonepayManualReviewPanel';
import WithdrawalPayoutRefundBanner from './WithdrawalPayoutRefundBanner';
import WithdrawalReviewIndicator from '../withdrawalRequest/WithdrawalReviewIndicator';
import type { WithdrawalRequest } from '../../../types';

interface TransactionDetailsProps {
  transaction: Transaction | null;
  onClose: () => void;
  /** Per-game rows from GET /admin/transactions/:id/allocations (multi-game deposits). */
  allocations?: TransactionAllocation[];
  allocationsLoading?: boolean;
  /** `modal` = overlay as before; `page` = full-page content (no backdrop); `embedded` = nested in parent panel/modal (no backdrop, no separate close button). */
  variant?: 'modal' | 'page' | 'embedded';
  /** List page URL when variant is `page` (preserves tab scope via query params). */
  listHref?: string;
  /** Refetch transaction detail after GGUS manual settlement. */
  onTransactionRefresh?: () => void;
}

/** Hide empty, very short single-token strings, and obvious noise (e.g. "aaaa"). */
const isMeaningfulDescription = (raw: string | undefined | null): boolean => {
  const t = (raw || '').trim();
  if (t.length === 0) return false;
  if (t.length < 8 && !/\s/.test(t)) return false;
  if (/^(.)\1{3,}$/.test(t)) return false;
  return true;
};

function toWithdrawalReviewShape(transaction: Transaction): WithdrawalRequest | null {
  if (transaction.type !== 'withdrawal') return null;
  return {
    transactionId: transaction.transactionId,
    userId: transaction.userId,
    user: transaction.user
      ? {
          id: transaction.user.id,
          email: transaction.user.email,
          phone: transaction.user.phone ?? null,
          profile: { firstName: '', lastName: '' },
        }
      : null,
    amount: Number(transaction.amount),
    currency: transaction.currency,
    description: transaction.description,
    status: (transaction.status as WithdrawalRequest['status']) || 'pending',
    flaggedInfo: transaction.flaggedInfo ?? null,
    createdAt: transaction.created_at,
    updatedAt: transaction.updated_at,
  };
}

function isGameWithdrawalDetail(transaction: Transaction): boolean {
  return (
    transaction.type === 'withdrawal' &&
    (transaction.paymentMethod === 'game_withdrawal' ||
      !!transaction.gameWithdrawalDetail?.gameProvider ||
      !!transaction.gameProvider)
  );
}

function formatGameWalletDirection(direction?: string | null): string {
  const d = String(direction || '').toLowerCase();
  if (d === 'game_to_master') return 'Game → Master wallet';
  if (d === 'master_to_game') return 'Master → Game wallet';
  if (!d) return '—';
  return d.replace(/_/g, ' ');
}

function GameWithdrawalDetailSection({ transaction }: { transaction: Transaction }) {
  const detail = transaction.gameWithdrawalDetail;
  const provider = detail?.gameProvider || transaction.gameProvider || null;
  if (!provider?.name && !provider?.code) return null;

  const providerLabel = provider.name || provider.code || 'Unknown game';
  const amountDisplay = getTransactionAmountDisplay(transaction);
  const credits =
    amountDisplay.mode === 'credits_only'
      ? amountDisplay.credits
      : Math.abs(Number(detail?.amount ?? transaction.amount ?? 0));
  const status = detail?.status || transaction.status;

  return (
    <div className="space-y-2">
      <h4 className="text-xs font-semibold flex items-center gap-1.5">
        <Gamepad2 className="h-3.5 w-3.5" />
        Game withdrawal
      </h4>
      <div className="rounded-md border bg-card px-3 py-2 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
          <div>
            <div className="text-[10px] uppercase text-muted-foreground">Game</div>
            <div className="mt-0.5 font-medium">{providerLabel}</div>
            {provider.code ? (
              <div className="text-[10px] text-muted-foreground font-mono">{provider.code}</div>
            ) : null}
          </div>
          <div>
            <div className="text-[10px] uppercase text-muted-foreground">Credits</div>
            <div className="mt-0.5 font-medium tabular-nums">{formatCreditsAmount(credits)} credits</div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-muted-foreground">Direction</div>
            <div className="mt-0.5 font-medium">{formatGameWalletDirection(detail?.direction)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-muted-foreground">Settlement status</div>
            <div className="mt-0.5 font-medium capitalize">{status || '—'}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function BalanceToCreditsConversionBanner({ t }: { t: Transaction }) {
  const conversion = getBalanceToCreditsConversionDisplay(t);
  if (!conversion) return null;

  const creditsFormatted = conversion.creditAmount.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  const usdFormatted = conversion.usdAmount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return (
    <div className="rounded-md border border-violet-200 bg-violet-50/80 px-3 py-2 space-y-1.5">
      <h4 className="text-xs font-semibold text-violet-950">USD → credits conversion</h4>
      <p className="text-[11px] text-violet-900/90 leading-snug">
        {conversion.buyerRoleLabel} used USD wallet balance to receive credits ({conversion.formulaLine}).
      </p>
      <dl className="grid grid-cols-3 gap-2 text-[11px]">
        <div>
          <dt className="text-violet-800/80">Cost rate</dt>
          <dd className="font-semibold text-violet-950 tabular-nums">{conversion.costRatePercentLabel}</dd>
        </div>
        <div>
          <dt className="text-violet-800/80">USD spent</dt>
          <dd className="font-semibold text-violet-950 tabular-nums">${usdFormatted}</dd>
        </div>
        <div>
          <dt className="text-violet-800/80">Credits</dt>
          <dd className="font-semibold text-violet-950 tabular-nums">{creditsFormatted}</dd>
        </div>
      </dl>
    </div>
  );
}

function MetaCell({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-xs font-medium text-foreground break-words">{children}</dd>
    </div>
  );
}

const TransactionDetails: React.FC<TransactionDetailsProps> = ({
  transaction,
  onClose,
  allocations,
  allocationsLoading = false,
  variant = 'modal',
  listHref = '/transactions',
  onTransactionRefresh,
}) => {
  const { timezone } = useTimezone();
  const allocationSummary = useMemo(() => {
    if (!transaction || !allocations?.length) return null;
    return summarizeTransactionAllocations(allocations, transaction.status);
  }, [transaction, allocations]);

  const cashFlowSteps = useMemo(() => {
    if (!transaction) return [];
    return buildTransactionCashFlowTimeline(transaction, timezone, {
      allocations: allocations?.length ? allocations : undefined,
      allocationSummary: allocationSummary ?? undefined,
    });
  }, [transaction, timezone, allocations, allocationSummary]);
  const lifecycle = useMemo(() => {
    if (!transaction) return null;
    return buildTransactionLifecycleFlow(transaction, {
      allocations: allocations?.length ? allocations : undefined,
    });
  }, [transaction, allocations]);

  if (!transaction) return null;

  const showAllocationsPanel =
    transactionNeedsAllocationsFetch(transaction) &&
    (allocationsLoading || (allocations && allocations.length > 0));

  const effectiveFulfillmentStatus = allocationSummary?.fulfillmentStatus ?? transaction.fulfillmentStatus;
  const payoutRefundInfo = getWithdrawalPayoutRefundInfo(transaction);
  const statusDisplay = payoutRefundInfo
    ? getAdminTransactionStatusDisplay(transaction)
    : effectiveFulfillmentStatus
      ? getAdminTransactionStatusDisplay({
          ...transaction,
          fulfillmentStatus: effectiveFulfillmentStatus,
        })
      : getAdminTransactionStatusDisplay(transaction);

  const adminNotesText = (transaction.adminNotes || '').trim();
  const failureReasonText = getRecordedFailureReason({
    errorMessage: transaction.errorMessage,
    syncMetadata: transaction.syncMetadata,
  });
  const allocationFailureText = allocationSummary?.failureMessages.join('\n') || '';
  const isPartialFulfillment = allocationSummary?.fulfillmentStatus === 'partial';
  const isFullAllocationFailure =
    allocationSummary?.fulfillmentStatus === 'failed' ||
    allocationSummary?.fulfillmentStatus === 'refunded' ||
    allocationSummary?.fulfillmentStatus === 'rejected';
  const showFailureBanner =
    shouldShowRecordedFailureBanner(transaction.status, failureReasonText) ||
    (isFullAllocationFailure && allocationFailureText.length > 0);
  const showAllocationIssuesBanner = isPartialFulfillment && allocationFailureText.length > 0;
  const showAdminNotesFooter = adminNotesText.length > 0;
  const paymentStatusDiffers =
    effectiveFulfillmentStatus && effectiveFulfillmentStatus !== transaction.status;
  const fulfillmentHint =
    allocationSummary && effectiveFulfillmentStatus === 'partial'
      ? `${allocationSummary.completed}/${allocationSummary.total} games credited`
      : null;

  const StatusIcon =
    statusDisplay.tone === 'success'
      ? CheckCircle
      : statusDisplay.tone === 'danger'
        ? XCircle
        : statusDisplay.tone === 'neutral'
          ? Ban
          : Clock;

  const meaningfulDesc = isMeaningfulDescription(transaction.description);
  const displayType = getTransactionDisplayType(transaction);
  const amountCols = applyWithdrawalPayoutRefundDetailAmount(
    getTransactionListAmountColumns(transaction),
    transaction,
    transaction.currency || 'USD',
  );
  const isWithdrawal = transaction.type === 'withdrawal';
  const withdrawalReviewShape = toWithdrawalReviewShape(transaction);
  const showCashFlowTimeline = cashFlowSteps.length > 0;

  const amountToneClass = (tone: 'positive' | 'negative' | 'neutral') => {
    if (tone === 'positive') return 'text-green-600';
    if (tone === 'negative') return 'text-red-600';
    return 'text-foreground';
  };

  const TypeIcon = isWithdrawal ? ArrowUpRight : ArrowDownRight;

  const isPage = variant === 'page';
  const isEmbedded = variant === 'embedded';
  const isStandaloneModal = variant === 'modal';

  const createdLabel = formatInTimezone(transaction.created_at, timezone);
  const updatedLabel = formatInTimezone(transaction.updated_at, timezone);
  const approvedLabel = transaction.approvedAt
    ? formatInTimezone(transaction.approvedAt, timezone)
    : null;

  const content = (
    <>
      {/* Compact summary strip */}
      <div className="rounded-lg border bg-card px-3 py-2.5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1 space-y-1.5">
            <div className="flex flex-wrap items-center gap-1.5">
              <span
                className={cn(
                  'inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-semibold',
                  displayType.badgeClassName,
                )}
              >
                <TypeIcon className="h-3 w-3 shrink-0" />
                {displayType.label}
              </span>
              <span
                className={cn(
                  'inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-semibold',
                  statusDisplay.className,
                )}
              >
                <StatusIcon className="h-3 w-3 shrink-0" />
                {statusDisplay.label}
              </span>
              {paymentStatusDiffers ? (
                <span className="rounded-md bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                  Pay: {transaction.status}
                  {fulfillmentHint ? ` · Fulfillment: ${fulfillmentHint}` : ''}
                </span>
              ) : null}
              {transaction.paymentMethod ? (
                <span className="rounded-md bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                  {transaction.paymentMethod}
                </span>
              ) : null}
              {transaction.provider ? (
                <GatewayBadge provider={transaction.provider} />
              ) : null}
            </div>
            {meaningfulDesc ? (
              <p className="text-xs text-muted-foreground leading-snug whitespace-pre-wrap">
                {transaction.description?.trim()}
              </p>
            ) : null}
            {transaction.transactionId ? (
              <IntegrationLogsLink tab="payment" transactionId={transaction.transactionId} />
            ) : null}
          </div>
          <div className="text-right shrink-0 space-y-0.5">
            {amountCols.usdLabel ? (
              <div>
                <div className={cn('text-lg font-bold tabular-nums leading-none', amountToneClass(amountCols.usdTone))}>
                  {amountCols.usdDirection === 'in' ? '+' : amountCols.usdDirection === 'out' ? '−' : ''}
                  {amountCols.usdLabel}
                  {amountCols.usdUnit ? (
                    <span className="ml-1 text-[10px] font-semibold uppercase text-muted-foreground">
                      {amountCols.usdUnit}
                    </span>
                  ) : null}
                </div>
                {amountCols.usdSubLabel ? (
                  <div className="mt-0.5 text-[10px] font-normal text-muted-foreground tabular-nums">
                    {amountCols.usdSubLabel}
                  </div>
                ) : null}
              </div>
            ) : null}
            {amountCols.creditLabel ? (
              <div
                className={cn('text-lg font-bold tabular-nums leading-none', amountToneClass(amountCols.creditTone))}
              >
                {amountCols.creditDirection === 'in' ? '+' : amountCols.creditDirection === 'out' ? '−' : ''}
                {amountCols.creditLabel}
                <span className="ml-1 text-[10px] font-semibold uppercase text-muted-foreground">
                  {amountCols.creditUnit || 'Credits'}
                </span>
              </div>
            ) : null}
          </div>
        </div>

        <dl className="mt-2.5 grid grid-cols-2 gap-x-3 gap-y-2 border-t pt-2.5 sm:grid-cols-3 lg:grid-cols-6">
          <MetaCell label="Created">{createdLabel || '—'}</MetaCell>
          <MetaCell label="Updated">{updatedLabel || '—'}</MetaCell>
          <MetaCell label="Action">{isWithdrawal ? 'Withdrawal' : 'Deposit'}</MetaCell>
          <MetaCell label="Status">{statusDisplay.label}</MetaCell>
          <MetaCell label="Approved">
            {approvedLabel ? (
              <span>
                {approvedLabel}
                {transaction.approvedBy ? (
                  <span className="block text-[10px] font-normal text-muted-foreground">
                    by {transaction.approvedBy}
                  </span>
                ) : null}
              </span>
            ) : (
              '—'
            )}
          </MetaCell>
          <MetaCell label="External ID">{transaction.externalTransactionId || '—'}</MetaCell>
        </dl>
      </div>

      {payoutRefundInfo ? (
        <WithdrawalPayoutRefundBanner
          refund={payoutRefundInfo}
          currency={transaction.currency || 'USD'}
        />
      ) : null}

      {showFailureBanner ? (
        <div
          role="alert"
          className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-950 flex gap-2"
        >
          <AlertTriangle className="h-4 w-4 shrink-0 text-red-600 mt-0.5" />
          <div className="min-w-0">
            <p className="font-semibold text-red-900">Failure reason</p>
            <p className="mt-0.5 whitespace-pre-wrap leading-snug">
              {failureReasonText || allocationFailureText}
            </p>
          </div>
        </div>
      ) : null}

      {showAllocationIssuesBanner ? (
        <div
          role="alert"
          className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950 flex gap-2"
        >
          <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 mt-0.5" />
          <div className="min-w-0">
            <p className="font-semibold text-amber-900">Allocation issues</p>
            {fulfillmentHint ? (
              <p className="mt-0.5 text-[11px] text-amber-800">{fulfillmentHint}</p>
            ) : null}
            <p className="mt-0.5 whitespace-pre-wrap leading-snug">{allocationFailureText}</p>
          </div>
        </div>
      ) : null}

      <TransactionFraudFlagBanner fraudFlags={transaction.fraudFlags} fraudFlag={transaction.fraudFlag} />

      {withdrawalReviewShape ? (
        <WithdrawalReviewIndicator withdrawal={withdrawalReviewShape} variant="banner" />
      ) : null}

      <BalanceToCreditsConversionBanner t={transaction} />

      {/* Lifecycle flowchart — primary process view */}
      {lifecycle ? (
        <TransactionLifecycleFlow
          steps={lifecycle.steps}
          flowKindLabel={getLifecycleFlowKindLabel(lifecycle.flowKind)}
        />
      ) : null}

      <GgusonepayManualReviewPanel transaction={transaction} onSettled={onTransactionRefresh} />

      {/* Money movement — secondary, compact title */}
      {showCashFlowTimeline ? (
        <TransactionCashFlowTimelineTree
          steps={cashFlowSteps}
          title="Money movement"
          className="bg-card [&_h3]:text-sm [&_.space-y-4]:space-y-2"
        />
      ) : null}

      {isGameWithdrawalDetail(transaction) ? (
        <GameWithdrawalDetailSection transaction={transaction} />
      ) : null}

      {showAdminNotesFooter ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
          <h4 className="text-xs font-semibold text-amber-950">Admin notes</h4>
          <p className="mt-1 text-xs text-amber-950 whitespace-pre-wrap leading-snug">{adminNotesText}</p>
        </div>
      ) : null}

      {showAllocationsPanel && allocations ? (
        <TransactionAllocationsPanel
          allocations={allocations}
          summary={allocationSummary}
          loading={allocationsLoading}
        />
      ) : null}

      {transaction.metadata?.allocations &&
      Array.isArray(transaction.metadata.allocations) &&
      transaction.metadata.allocations.length > 0 &&
      !showAllocationsPanel ? (
        <div className="space-y-2">
          <h4 className="text-xs font-semibold flex items-center gap-1.5">
            <Package className="h-3.5 w-3.5" />
            Allocations
          </h4>
          <div className="rounded-md border bg-card px-3 py-2 space-y-2">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
              <div>
                <div className="text-[10px] uppercase text-muted-foreground">Multi game</div>
                <div className="font-medium">{transaction.metadata.isMultiGame ? 'Yes' : 'No'}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-muted-foreground">Count</div>
                <div className="font-medium">
                  {transaction.metadata.allocationsCount || transaction.metadata.allocations.length}
                </div>
              </div>
              {transaction.metadata.email ? (
                <div>
                  <div className="text-[10px] uppercase text-muted-foreground">Email</div>
                  <div className="font-medium break-all">{transaction.metadata.email}</div>
                </div>
              ) : null}
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="h-8 text-xs">Game</TableHead>
                  <TableHead className="h-8 text-xs text-right">Credits</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transaction.metadata.allocations.map(
                  (allocation: { gameProviderCode?: string; amount?: number }, index: number) => (
                    <TableRow key={index}>
                      <TableCell className="py-1.5 text-xs">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <Gamepad2 className="text-muted-foreground shrink-0 h-3.5 w-3.5" />
                          <span className="truncate">{allocation.gameProviderCode || 'N/A'}</span>
                        </div>
                      </TableCell>
                      <TableCell className="py-1.5 text-right text-xs font-medium tabular-nums">
                        {formatCreditsAmount(Math.abs(Number(allocation.amount || 0)))} credits
                      </TableCell>
                    </TableRow>
                  ),
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      ) : null}

      {transaction.relatedTransactions && transaction.relatedTransactions.length > 0 ? (
        <div className="space-y-2">
          <h4 className="text-xs font-semibold">Related payment transactions</h4>
          <div className="rounded-md border bg-card divide-y">
            {transaction.relatedTransactions.map((related) => (
              <div
                key={related.id}
                className="flex flex-col gap-1 px-3 py-2 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0 space-y-0.5">
                  {related.refLink ? (
                    <Link to={related.refLink} className="text-xs font-medium text-primary hover:underline">
                      {related.summaryLabel || `${related.type} · ${related.status}`}
                    </Link>
                  ) : (
                    <div className="text-xs font-medium capitalize">
                      {related.summaryLabel || `${related.type} · ${related.status}`}
                    </div>
                  )}
                  {related.recipientDisplay ? (
                    <div className="text-[10px] text-muted-foreground">To {related.recipientDisplay}</div>
                  ) : null}
                  <div className="font-mono text-[10px] text-muted-foreground/80 break-all">
                    {related.transactionId}
                  </div>
                </div>
                <div className="text-xs sm:text-right font-medium tabular-nums">
                  {formatCashAmount(
                    Math.abs(Number(related.amount || 0)),
                    related.currency || transaction.currency || 'USD',
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {(transaction.linkMePayRequest || transaction.paymentResult) && (
        <details className="group rounded-md border bg-muted/20 px-3 py-2">
          <summary className="cursor-pointer list-none text-xs font-semibold">
            Raw payment payload{' '}
            <span className="text-[10px] font-normal text-muted-foreground group-open:hidden">(collapsed)</span>
          </summary>
          <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
            {transaction.linkMePayRequest ? (
              <div className="rounded-md border bg-muted/30 p-2">
                <h5 className="font-medium mb-1 text-[11px]">LinkMePay request</h5>
                <ScrollArea className="h-40 w-full rounded-md border bg-background">
                  <pre className="p-2 text-[10px] text-muted-foreground">
                    {JSON.stringify(transaction.linkMePayRequest, null, 2)}
                  </pre>
                </ScrollArea>
              </div>
            ) : null}
            {transaction.paymentResult ? (
              <div className="rounded-md border bg-muted/30 p-2">
                <h5 className="font-medium mb-1 text-[11px]">Payment result</h5>
                <ScrollArea className="h-40 w-full rounded-md border bg-background">
                  <pre className="p-2 text-[10px] text-muted-foreground">
                    {JSON.stringify(transaction.paymentResult, null, 2)}
                  </pre>
                </ScrollArea>
              </div>
            ) : null}
          </div>
        </details>
      )}

      {!isPage && isStandaloneModal ? (
        <>
          <Separator />
          <div className="flex justify-end pt-1">
            <Button variant="outline" size="sm" onClick={onClose}>
              Close
            </Button>
          </div>
        </>
      ) : null}
    </>
  );

  const card = (
    <Card
      className={cn(
        'mx-auto flex w-full flex-col overflow-hidden',
        isStandaloneModal && 'max-w-5xl max-h-[90vh]',
        isEmbedded && 'max-h-[min(70vh,560px)] w-full border-primary/20 bg-muted/10',
      )}
    >
      <CardHeader className="flex flex-row items-start justify-between space-y-0 py-3 px-4 shrink-0 gap-2">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <div className="min-w-0">
            <CardTitle className="text-base truncate">
              {isEmbedded ? 'Linked payment transaction' : 'Transaction details'}
            </CardTitle>
            <p className="mt-0.5 font-mono text-[11px] text-muted-foreground break-all">
              {transaction.transactionId}
            </p>
          </div>
        </div>
        {isStandaloneModal ? (
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close">
            <X />
          </Button>
        ) : null}
      </CardHeader>
      <CardContent className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 pb-4 pt-0">{content}</CardContent>
    </Card>
  );

  if (isPage) {
    return (
      <div className="mx-auto w-full max-w-5xl space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" asChild className="-ml-2 h-8">
                <Link to={listHref}>
                  <ArrowLeft aria-hidden />
                  Back
                </Link>
              </Button>
              <h2 className="text-base font-semibold truncate">Transaction details</h2>
            </div>
            <p className="mt-0.5 font-mono text-[11px] text-muted-foreground break-all">
              {transaction.transactionId}
            </p>
          </div>
        </div>

        <div className="space-y-3">{content}</div>
      </div>
    );
  }

  if (isEmbedded) {
    return <div className="w-full">{card}</div>;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 !mt-0 p-4">
      {card}
    </div>
  );
};

export default TransactionDetails;
